import { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { crewStore } from '@/stores/CrewStore'
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const CrewExecutePage = observer(() => {
  const { name } = useParams<{ name: string }>()
  const navigate = useNavigate()
  const [inputMode, setInputMode] = useState<'form' | 'json'>('form')
  const [promptInput, setPromptInput] = useState('')
  const [jsonInput, setJsonInput] = useState(JSON.stringify({ prompt: '', context: {} }, null, 2))
  const [projectId, setProjectId] = useState('')
  const [additionalContext, setAdditionalContext] = useState('')
  const resultsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (name) {
      crewStore.fetchCrew(name)
    }
  }, [name])

  const crew = crewStore.selectedCrew

  const handleExecute = async () => {
    if (!name) return

    try {
      let prompt: string
      let context: Record<string, any> = {}

      if (inputMode === 'json') {
        try {
          const parsed = JSON.parse(jsonInput)
          prompt = parsed.prompt || ''
          context = parsed.context || {}
        } catch (e) {
          toast.error('Invalid JSON input')
          return
        }
      } else {
        if (!promptInput.trim()) {
          toast.error('Prompt is required')
          return
        }
        prompt = promptInput
        if (projectId) {
          context.projectId = projectId
        }
        if (additionalContext) {
          try {
            const additionalObj = JSON.parse(additionalContext)
            context = { ...context, ...additionalObj }
          } catch (e) {
            toast.error('Additional context must be valid JSON')
            return
          }
        }
      }

      await crewStore.invokeCrew(name, prompt, context)
      toast.success('Crew execution completed!')

      // Scroll to results
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch (error: any) {
      toast.error(error.message || 'Failed to execute crew')
    }
  }

  const getProcessTypeIcon = (processType: string) => {
    const icons: Record<string, typeof ArrowsRightLeftIcon> = {
      sequential: ArrowsRightLeftIcon,
      parallel: Squares2X2Icon,
      hierarchical: ChartBarSquareIcon,
    }
    return icons[processType] || ArrowsRightLeftIcon
  }

  if (crewStore.isLoading && !crew) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading crew...</p>
        </div>
      </div>
    )
  }

  if (!crew) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="text-center p-8">
          <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-2">Crew Not Found</h2>
          <p className="text-muted-foreground mb-4">The crew "{name}" could not be found.</p>
          <Button variant="primary" onClick={() => navigate('/crews')}>
            Back to Crews
          </Button>
        </Card>
      </div>
    )
  }

  if (!crew.enabled) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="text-center p-8">
          <XCircleIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-2">Crew Disabled</h2>
          <p className="text-muted-foreground mb-4">This crew is currently disabled and cannot be executed.</p>
          <Button variant="primary" onClick={() => navigate(`/crews/${name}`)}>
            View Crew Details
          </Button>
        </Card>
      </div>
    )
  }

  const ProcessIcon = getProcessTypeIcon(crew.process_type)
  const lastExecution = crewStore.lastExecution

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/crews/${name}`)}>
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back to Crew
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Execute: {crew.display_name}</h1>
            <p className="text-muted-foreground mt-1">{crew.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Input Section */}
          <Card>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Execution Input</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setInputMode('form')}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      inputMode === 'form'
                        ? 'bg-primary-500/15 text-primary-700 font-medium'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    Form
                  </button>
                  <button
                    onClick={() => setInputMode('json')}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      inputMode === 'json'
                        ? 'bg-primary-500/15 text-primary-700 font-medium'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    JSON
                  </button>
                </div>
              </div>

              {inputMode === 'form' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Prompt <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={promptInput}
                      onChange={(e) => setPromptInput(e.target.value)}
                      placeholder="Enter your prompt for the crew..."
                      rows={6}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Describe the task you want the crew to accomplish
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Project ID (optional)
                    </label>
                    <input
                      type="text"
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      placeholder="e.g., proj_abc123"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Additional Context (optional JSON)
                    </label>
                    <textarea
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder='{"key": "value"}'
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    JSON Input
                  </label>
                  <textarea
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    placeholder='{"prompt": "Your prompt here", "context": {}}'
                    rows={12}
                    className="w-full px-3 py-2 border border-border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Provide input as JSON with "prompt" and optional "context" fields
                  </p>
                </div>
              )}

              <Button
                variant="primary"
                onClick={handleExecute}
                disabled={crewStore.isInvoking}
                className="w-full"
              >
                {crewStore.isInvoking ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Executing...
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4 mr-2" />
                    Execute Crew
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Results Section */}
          {lastExecution && (
            <div ref={resultsRef}>
              <Card>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">Execution Result</h2>
                    <div className="flex items-center gap-2">
                      {lastExecution.success ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-red-500" />
                      )}
                      <Badge variant={lastExecution.success ? 'success' : 'error'}>
                        {lastExecution.success ? 'Success' : 'Failed'}
                      </Badge>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Request ID</p>
                      <p className="text-sm font-mono text-foreground">
                        {lastExecution.metadata.request_id?.slice(0, 8) || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Tokens Used</p>
                      <p className="text-sm font-semibold text-foreground">
                        {lastExecution.metadata.token_count?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Quality Score</p>
                      <p className="text-sm font-semibold text-foreground">
                        {lastExecution.metadata.quality_score?.toFixed(1) || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Error */}
                  {lastExecution.error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-semibold text-red-600 mb-1">Error</h3>
                          <p className="text-sm text-red-600">{lastExecution.error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  {lastExecution.summary && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <DocumentTextIcon className="w-5 h-5 text-muted-foreground" />
                        <h3 className="text-sm font-semibold text-foreground">Summary</h3>
                      </div>
                      <div className="bg-muted/50 border border-border rounded-lg p-4">
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {lastExecution.summary}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  {lastExecution.content && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <DocumentTextIcon className="w-5 h-5 text-muted-foreground" />
                        <h3 className="text-sm font-semibold text-foreground">Content</h3>
                      </div>
                      <div className="bg-muted/50 border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                          {lastExecution.content}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Findings */}
                  {lastExecution.findings && lastExecution.findings.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <LightBulbIcon className="w-5 h-5 text-yellow-600" />
                        <h3 className="text-sm font-semibold text-foreground">
                          Findings ({lastExecution.findings.length})
                        </h3>
                      </div>
                      <div className="space-y-2">
                        {lastExecution.findings.map((finding, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
                          >
                            <p className="text-sm text-yellow-600">
                              {typeof finding === 'string' ? finding : JSON.stringify(finding)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {lastExecution.recommendations && lastExecution.recommendations.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600" />
                        <h3 className="text-sm font-semibold text-foreground">
                          Recommendations ({lastExecution.recommendations.length})
                        </h3>
                      </div>
                      <div className="space-y-2">
                        {lastExecution.recommendations.map((rec, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                          >
                            <p className="text-sm text-blue-600">
                              {typeof rec === 'string' ? rec : JSON.stringify(rec)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confidence */}
                  {lastExecution.confidence !== undefined && (
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <SparklesIcon className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="text-sm text-muted-foreground">Confidence Score</p>
                        <p className="text-lg font-semibold text-foreground">
                          {(lastExecution.confidence * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Crew Info */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Crew Info</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Process Type</p>
                <div className="flex items-center gap-2 mt-1">
                  <ProcessIcon className="w-4 h-4 text-primary-600" />
                  <span className="text-sm font-medium text-foreground capitalize">
                    {crew.process_type}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Domain</p>
                <div className="mt-1">
                  <Badge variant="primary" size="sm">
                    {crew.domain}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Version</p>
                <p className="text-sm text-foreground">{crew.version}</p>
              </div>
            </div>
          </Card>

          {/* Team Members */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Team Members ({crew.agents.length})
            </h3>
            <div className="space-y-2">
              {crew.agents.map((agent, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                >
                  <span className="text-xs font-mono text-muted-foreground">{index + 1}</span>
                  <UserIcon className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {agent.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{agent.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Agents Involved in Last Execution */}
          {lastExecution?.metadata?.agents_involved && lastExecution.metadata.agents_involved.length > 0 && (
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Agents Used</h3>
              <div className="flex flex-wrap gap-2">
                {lastExecution.metadata.agents_involved.map((agent, idx) => (
                  <Badge key={idx} variant="success" size="sm">
                    {agent}
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Info Box */}
          <Card className="bg-blue-500/10 border-blue-500/20">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-600 mb-1">How it works</h3>
                <p className="text-xs text-blue-600">
                  This crew uses {crew.process_type} coordination. Your prompt will be processed
                  by {crew.agents.length} specialized agents working together to produce a
                  comprehensive result.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default CrewExecutePage
